import React from 'react';
import { useState, useMemo } from 'react';
import { addSearchIndex, removeSearchIndex } from '../../services/searchIndexer';
import StatusMultiSelect from '../StatusMultiSelect';
import TableHeader from './TableHeader';
import TableContent from './TableContent';
import extractColumns from './extractColumnsFromData';
import './Table.scss';

const unique = (value, index, self) => self.indexOf(value) === index;
/** @type {string[]} */
const initialStatuses = [];

/**
 * @template T
 * @param {{
 *  data: T[];
 *  columns?: CommonClient2.TableStaticColumn[];
 *  hiddenColumns?: string[];
 *  tableId?: string;
 *  statusFilterField?: string;
 *  nameColumnKey?: string;
 *  nameColumnChipKey?: string;
 *  defaultSortColumnKey?: string;
 *  defaultSortType?: CommonClient2.SortType;
 *  isRowsSelectable?: boolean;
 *  dateColumnKeys?: string[];
 *  dateTimeColumnKeys?: string[];
 *  createLabel?: string
 *  onCreate?: (event: React.MouseEvent<HTMLElement>) => void,
 *  onEdit?: (event: React.MouseEvent<HTMLElement>, value: T) => void,
 *  onDuplicate?: (event: React.MouseEvent<HTMLElement>, value: T) => void,
 *  onDelete?: (event: React.MouseEvent<HTMLElement>, value: T) => void,
 *  onManage?: (event: React.MouseEvent<HTMLElement>, value: T) => void,
 *  onAddRound?: (event: React.MouseEvent<HTMLElement>, value: T) => void,
 *  onChange?: (event: React.ChangeEvent, value: T) => void,
 *  onNameCellClick?: (event: React.MouseEvent<HTMLElement>, value: T) => void;
 *  disableSearch?: boolean,
 *  fillAvailableHeight?: boolean;
 *  dropdownPlaceholder?: string;
 *  useSimpleDropdown?: boolean;
 *  dropdrownPlaceholder?: string;
 *  treeChildrenKey?: string; // to be use with isTree
 *  rowFilterProps?: any;
 *  downloadCsvProps?: {onClick: function; isDisabled: boolean;};
 * } & import('rsuite-table').TableProps} param0
 * @example <Table
                data={bidSetups}
                columns={tableColumns}
                id="admin-settings-table"
                rowClassName="table-row"
                autoHeight
            />
 */
const Table = ({
    data,
    columns,
    hiddenColumns = [],
    statusFilterField,
    nameColumnKey,
    nameColumnChipKey,
    defaultSortColumnKey = '',
    defaultSortType,
    dateColumnKeys,
    dateTimeColumnKeys,
    isRowsSelectable = false,
    onNameCellClick,
    createLabel,
    onCreate,
    onEdit,
    onDuplicate,
    onDelete,
    onManage,
    onAddRound,
    onChange,
    disableSearch = false,
    fillAvailableHeight = false,
    useSimpleDropdown = false,
    dropdrownPlaceholder = '',
    treeChildrenKey,
    rowFilterProps,
    downloadCsvProps,
    ...others
}) => {
    data = data || [];
    const stringifiedData = JSON.stringify(data);
    const [filterTerm, setFilterTerm] = useState('');

    const [selectedStatus, setSelectedStatuses] = useState(initialStatuses);
    const availableStatuses = useMemo(
        () => {
            /** @type {string[]} */
            const allAvailableStatuses = data.map((d) => d[statusFilterField || 'status']).filter(unique);
            return allAvailableStatuses;
        },
        [stringifiedData],
    );

    const indexedData = useMemo(() => data.map(addSearchIndex), [stringifiedData]);

    columns = !columns?.length ? extractColumns(data) : columns;
    if (hiddenColumns?.length) {
        columns = columns.filter((c) => !hiddenColumns.includes(c.key));
    }

    if (dateColumnKeys?.length) {
        dateColumnKeys.forEach((dck) => {
            const dateColumn = columns?.find((c) => c.key === dck) || { dataValueType: '' };
            dateColumn.dataValueType = 'DATE';
        });
    }

    if (dateTimeColumnKeys?.length) {
        dateTimeColumnKeys.forEach((dtck) => {
            const dateTimeColumn = columns?.find((c) => c.key === dtck) || { dataValueType: '' };
            dateTimeColumn.dataValueType = 'DATETIME';
        });
    }

    let filteredData = !statusFilterField
        ? indexedData
        : indexedData.filter((i) => selectedStatus.includes(i[statusFilterField]));

    filteredData = !filterTerm
        ? filteredData
        : filteredData.filter((i) => i.searchIndex?.includes(filterTerm.toLocaleLowerCase()));

    const cleanedUpFilteredData = filteredData.map(removeSearchIndex);

    const onSearch = disableSearch
        ? undefined
        : (event, /** @type {string} */ value) => setFilterTerm(value);

    /** @param {string[]} updatedSelectedStatuses */
    const onStatusSelectionChange = (updatedSelectedStatuses) => setSelectedStatuses(updatedSelectedStatuses);

    return (
        <div className="core--table">
            <TableHeader
                onSearch={onSearch}
                rowFilterProps={rowFilterProps}
                downloadCsvProps={downloadCsvProps}
                onCreate={onCreate}
                createLabel={createLabel}
            >
                {
                    !statusFilterField
                        ? <></>
                        : (
                            <StatusMultiSelect
                                allStatuses={availableStatuses}
                                onStatusChange={onStatusSelectionChange}
                                useSimpleDropdown={useSimpleDropdown}
                                placeholder={dropdrownPlaceholder}
                            />
                        )
                }
            </TableHeader>
            <TableContent
                columns={columns}
                data={cleanedUpFilteredData}
                defaultSortColumnKey={defaultSortColumnKey}
                defaultSortType={defaultSortType}
                fillAvailableHeight={fillAvailableHeight}
                isRowsSelectable={isRowsSelectable}
                nameColumnChipKey={nameColumnChipKey}
                nameColumnKey={nameColumnKey}
                onAddRound={onAddRound}
                onChange={onChange}
                onDelete={onDelete}
                onDuplicate={onDuplicate}
                onEdit={onEdit}
                onManage={onManage}
                onNameCellClick={onNameCellClick}
                statusFilterField={statusFilterField}
                treeChildrenKey={treeChildrenKey}
                {...others}
            />
        </div>
    );
};

export default Table;
